/**
 * xlsxExport.js - "Export Excel" for the Projects and Opportunities tables
 * (round 2, 10/9/26): exactly what is on screen, as a real .xlsx WITH the
 * highlight rules - fills, text colours and bold on the matching cells and
 * rows - the person's column widths, a bold frozen header row, money as
 * numbers with a currency format and dates as real dates.
 *
 * Why it is written by hand: the installed SheetJS (xlsx 0.18.5, the
 * community edition) reads styles but silently drops them on write, and no
 * other spreadsheet library is installed (no new dependencies). An .xlsx is
 * a zip of a few XML files, so buildXlsx writes those files - one sheet, a
 * de-duplicated style table - and storeZip packs them (stored, no
 * compression: Excel and SheetJS read that fine). Tests read the result back
 * with SheetJS.
 *
 * PURE apart from downloadXlsx.
 */
import { dayKey } from '../ui/DateFilterPopover';
import { rowHighlights, cellLook } from './highlights';
import { downloadBlob } from '../../utils/exportCsv';

/* ── the zip (stored entries) ─────────────────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
function utf8(str) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
  const bin = unescape(encodeURIComponent(str));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/** files: { path: string } -> the zip's bytes. */
export function storeZip(files) {
  const entries = Object.entries(files).map(([name, text]) => {
    const data = utf8(text);
    return { name: utf8(name), data, crc: crc32(data) };
  });
  const d = new Date();
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  const size = entries.reduce((n, e) => n + 30 + e.name.length + e.data.length + 46 + e.name.length, 0) + 22;
  const out = new Uint8Array(size);
  const v = new DataView(out.buffer);
  let at = 0;
  const u16 = (x) => {
    v.setUint16(at, x, true);
    at += 2;
  };
  const u32 = (x) => {
    v.setUint32(at, x, true);
    at += 4;
  };
  const bytes = (b) => {
    out.set(b, at);
    at += b.length;
  };
  const offsets = [];
  for (const e of entries) {
    offsets.push(at);
    u32(0x04034b50);
    u16(20);
    u16(0x0800);
    u16(0);
    u16(time);
    u16(date);
    u32(e.crc);
    u32(e.data.length);
    u32(e.data.length);
    u16(e.name.length);
    u16(0);
    bytes(e.name);
    bytes(e.data);
  }
  const cd = at;
  entries.forEach((e, i) => {
    u32(0x02014b50);
    u16(20);
    u16(20);
    u16(0x0800);
    u16(0);
    u16(time);
    u16(date);
    u32(e.crc);
    u32(e.data.length);
    u32(e.data.length);
    u16(e.name.length);
    u16(0);
    u16(0);
    u16(0);
    u16(0);
    u32(0);
    u32(offsets[i]);
    bytes(e.name);
  });
  const cdSize = at - cd;
  u32(0x06054b50);
  u16(0);
  u16(0);
  u16(entries.length);
  u16(entries.length);
  u32(cdSize);
  u32(cd);
  u16(0);
  return out;
}

/* ── the workbook ─────────────────────────────────────────────────── */

const esc = (s) =>
  String(s)
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFD]/g, '') // characters XML cannot hold
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
const argb = (hex) => `FF${String(hex).replace('#', '').toUpperCase()}`;
const colName = (i) => {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};
const BUILTIN_FMT = { '0%': 9, 'm/d/yyyy': 14 };
const HEADER = { bold: true, fill: '#E8EFE0' };

/**
 * spec = { sheetName, columns: [{ header, widthPx, headerSize? }], rows: [[cell]] } with
 * cell = { v, t: 's' | 'n' | 'd', fmt?, fill?, font?, bold?, size?, align? } ('d':
 * v is an Excel day number; size in points, 11 when not set; align left /
 * center / right). freezeCols: columns frozen at the left with the header. Returns { bytes, parts } - parts are the XML files, by path.
 */
export function buildXlsx({ sheetName = 'Sheet1', columns, rows, freezeCols = 0 }) {
  const fonts = ['<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>'];
  const fontKey = new Map([['|', 0]]);
  const fills = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>'];
  const fillKey = new Map([['', 0]]);
  const numFmts = [];
  const fmtKey = new Map();
  const xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  const xfKey = new Map([['0|0|0', 0]]);
  const styleOf = (c) => {
    const fk = c.size && c.size !== 11 ? `${c.font || ''}|${c.bold ? 'b' : ''}|${c.size}` : `${c.font || ''}|${c.bold ? 'b' : ''}`;
    if (!fontKey.has(fk)) {
      fontKey.set(fk, fonts.length);
      fonts.push(
        `<font>${c.bold ? '<b/>' : ''}<sz val="${c.size || 11}"/>${c.font ? `<color rgb="${argb(c.font)}"/>` : ''}<name val="Calibri"/><family val="2"/></font>`
      );
    }
    const fl = c.fill || '';
    if (!fillKey.has(fl)) {
      fillKey.set(fl, fills.length);
      fills.push(`<fill><patternFill patternType="solid"><fgColor rgb="${argb(fl)}"/><bgColor indexed="64"/></patternFill></fill>`);
    }
    let nf = 0;
    if (c.fmt) {
      if (BUILTIN_FMT[c.fmt] != null) nf = BUILTIN_FMT[c.fmt];
      else {
        if (!fmtKey.has(c.fmt)) {
          fmtKey.set(c.fmt, 164 + numFmts.length);
          numFmts.push(`<numFmt numFmtId="${164 + numFmts.length}" formatCode="${esc(c.fmt)}"/>`);
        }
        nf = fmtKey.get(c.fmt);
      }
    }
    const al = ['left', 'center', 'right'].includes(c.align) ? c.align : '';
    const key = `${nf}|${fontKey.get(fk)}|${fillKey.get(fl)}|${al}`;
    if (!xfKey.has(key)) {
      xfKey.set(key, xfs.length);
      const attrs = `numFmtId="${nf}" fontId="${fontKey.get(fk)}" fillId="${fillKey.get(fl)}" borderId="0" xfId="0"${nf ? ' applyNumberFormat="1"' : ''}${fontKey.get(fk) ? ' applyFont="1"' : ''}${fillKey.get(fl) ? ' applyFill="1"' : ''}`;
      xfs.push(al ? `<xf ${attrs} applyAlignment="1"><alignment horizontal="${al}"/></xf>` : `<xf ${attrs}/>`);
    }
    return xfKey.get(key);
  };
  const cellXml = (c, r, i) => {
    const ref = `${colName(i)}${r}`;
    const s = styleOf(c);
    const sAttr = s ? ` s="${s}"` : '';
    if ((c.t === 'n' || c.t === 'd') && Number.isFinite(c.v)) return `<c r="${ref}"${sAttr}><v>${c.v}</v></c>`;
    const text = c.v == null ? '' : String(c.v);
    if (!text) return s ? `<c r="${ref}"${sAttr}/>` : '';
    return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${esc(text)}</t></is></c>`;
  };
  const all = [columns.map((c) => ({ v: c.header, t: 's', ...HEADER, size: c.headerSize })), ...rows];
  const sheetData = all
    .map((cells, ri) => `<row r="${ri + 1}">${cells.map((c, ci) => cellXml(c || { v: '' }, ri + 1, ci)).join('')}</row>`)
    .join('');
  const cols = columns
    .map((c, i) => {
      const chars = Math.max(4, Math.round((((c.widthPx || 80) - 5) / 7) * 100) / 100);
      return `<col min="${i + 1}" max="${i + 1}" width="${chars}" customWidth="1"/>`;
    })
    .join('');

  const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
  const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
  const parts = {
    '[Content_Types].xml':
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
      '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
      '</Types>',
    '_rels/.rels':
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      `<Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    'xl/workbook.xml':
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      `<workbook xmlns="${NS}" xmlns:r="${REL}"><sheets><sheet name="${esc(
        String(sheetName)
          .replace(/[\\/?*[\]:]/g, ' ')
          .slice(0, 31)
      )}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    'xl/_rels/workbook.xml.rels':
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      `<Relationship Id="rId1" Type="${REL}/worksheet" Target="worksheets/sheet1.xml"/>` +
      `<Relationship Id="rId2" Type="${REL}/styles" Target="styles.xml"/></Relationships>`,
    'xl/worksheets/sheet1.xml':
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      `<worksheet xmlns="${NS}" xmlns:r="${REL}">` +
      (freezeCols > 0
        ? `<sheetViews><sheetView workbookViewId="0"><pane xSplit="${freezeCols}" ySplit="1" topLeftCell="${colName(freezeCols)}2" activePane="bottomRight" state="frozen"/></sheetView></sheetViews>`
        : '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>') +
      '<sheetFormatPr defaultRowHeight="15"/>' +
      `<cols>${cols}</cols><sheetData>${sheetData}</sheetData></worksheet>`,
  };
  // styles last: every cell above has registered its style by now
  parts['xl/styles.xml'] =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    `<styleSheet xmlns="${NS}">` +
    (numFmts.length ? `<numFmts count="${numFmts.length}">${numFmts.join('')}</numFmts>` : '') +
    `<fonts count="${fonts.length}">${fonts.join('')}</fonts>` +
    `<fills count="${fills.length}">${fills.join('')}</fills>` +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>` +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>';
  return { bytes: storeZip(parts), parts };
}

/* ── a table on screen -> the sheet ───────────────────────────────── */

const TOTALS_LOOK = { bold: true, fill: '#F4F1EC' }; // = the table's bottom totals row
// A column's font sizes (px on screen) in the sheet: the table's own sizes -
// 10.5px cells, 9.5px headers - are Excel's 11pt, and a size the person set
// scales from there (14px cells -> 14.5pt), to the half point.
const CELL_PX = 10.5;
const HEAD_PX = 9.5;
export const ptOf = (px, base) => (Number(px) > 0 ? Math.round(((11 * Number(px)) / base) * 2) / 2 : undefined);
/** The Excel format for money: $ on the main tabs, the record's code on GLOBAL KSA. */
export const moneyFormat = (code) =>
  !code || code === 'USD' ? '"$"#,##0.00' : /^[A-Z]{3}$/.test(code) ? `#,##0.00 "${code}"` : '#,##0.00';

/**
 * One export text (the CSV's, utils/exportCsv conventions) -> a typed cell:
 * money a number with the currency format, a percent column a fraction
 * with 0%, a date column an Excel day, ="0012" text kept as text.
 */
export function typedCell(def, text, currency) {
  const s = text == null ? '' : String(text);
  if (s === '') return { v: '', t: 's' };
  if (def.kind === 'number') {
    if (def.percent) {
      const m = /^(-?\d+(?:\.\d+)?)%$/.exec(s.trim());
      if (m) return { v: Number(m[1]) / 100, t: 'n', fmt: '0%' };
    } else if (Number.isFinite(Number(s))) {
      return { v: Number(s), t: 'n', fmt: moneyFormat(currency) };
    }
  }
  if (def.kind === 'date') {
    const k = dayKey(s);
    if (k) {
      const [y, mo, d] = k.split('-').map(Number);
      return { v: Date.UTC(y, mo - 1, d) / 86400000 + 25569, t: 'd', fmt: 'm/d/yyyy' };
    }
  }
  const m = /^="([\s\S]*)"$/.exec(s);
  return { v: m ? m[1].replace(/""/g, '"') : s, t: 's' };
}

/**
 * The sheet for exactly what is on screen: the visible columns in order (at
 * the person's widths), the rows as filtered and sorted, each cell styled
 * with its highlight look (lookOf(row, col) -> { fill, font, bold } | null),
 * and the totals row last.
 */
export function exportViewSheet(
  cols,
  rows,
  valueOf,
  {
    sheetName,
    totals = null,
    totalsLabel = 'Totals',
    lookOf = () => null,
    widths = {},
    fonts = {},
    currencyOf = () => null,
    totalsCurrency = null,
    align = {},
    baseFont = null,
    totalsAt = 'bottom',
    pinned = [],
  } = {}
) {
  const out = cols.filter((c) => !c.noExport);
  // a column's own size, else the table's own (Appearance > Text size), else 11pt
  const cellPt = (col) => ptOf(fonts[col] && fonts[col].cell, CELL_PX) || ptOf(baseFont, CELL_PX);
  const sized = (col, cell) => {
    const pt = cellPt(col);
    const withSize = pt && pt !== 11 ? { ...cell, size: pt } : cell;
    return align[col] ? { ...withSize, align: align[col] } : withSize;
  };
  const sheetRows = rows.map((r) =>
    out.map((c) => sized(c.col, { ...typedCell(c, valueOf(r, c.col), currencyOf(r)), ...(lookOf(r, c.col) || {}) }))
  );
  if (totals && rows.length && totalsAt !== 'none') {
    let labelled = false;
    // where the totals show on screen: under the header (top) or last
    (totalsAt === 'top' ? (r) => sheetRows.unshift(r) : (r) => sheetRows.push(r))(
      out.map((c) => {
        if (c.col in totals) return sized(c.col, { ...typedCell(c, totals[c.col], totalsCurrency), ...TOTALS_LOOK });
        if (!labelled) {
          labelled = true;
          return sized(c.col, { v: totalsLabel, t: 's', ...TOTALS_LOOK });
        }
        return sized(c.col, { v: '', t: 's', ...TOTALS_LOOK });
      })
    );
  }
  return {
    sheetName,
    // the frozen columns are first on screen; freeze the same ones in Excel
    freezeCols: out.filter((c) => pinned.includes(c.col)).length,
    columns: out.map((c) => ({
      header: c.exportLabel || c.label,
      widthPx: widths[c.col] || Math.max(c.minWidth || 80, 70),
      headerSize: ptOf(fonts[c.col] && fonts[c.col].head, HEAD_PX),
    })),
    rows: sheetRows,
  };
}

/** Save the workbook (the browser download). */
export function downloadXlsx(filename, bytes) {
  downloadBlob(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

/**
 * A table's "Export Excel": the view's visible columns, widths and highlight
 * rules over the rows on screen. rawValue(row, col) is what the rules test
 * (the same function the table paints with); the rest as exportViewSheet.
 */
export function viewWorkbook(view, rows, valueOf, rawValue, opts = {}) {
  const looks = new Map();
  // opts.rowLook(row): a whole row's own look under the highlights (a main /
  // sub-project record's group-row colour, 10/14/26); a highlight wins over it
  const { rowLook = null, ...rest } = opts;
  const lookOf = (r, col) => {
    const base = rowLook ? rowLook(r) : null;
    if (!view.highlights.length) return base;
    if (!looks.has(r))
      looks.set(
        r,
        rowHighlights(view.highlights, (c) => rawValue(r, c))
      );
    const hl = cellLook(looks.get(r), col);
    if (!base) return hl;
    return hl ? { ...base, ...Object.fromEntries(Object.entries(hl).filter(([, v]) => v != null && v !== false)) } : base;
  };
  const look = view.look || {};
  const totalsAt = look.totals === 'none' ? 'none' : look.totals === 'top' ? 'top' : 'bottom';
  return buildXlsx(
    exportViewSheet(view.visible, rows, valueOf, {
      ...rest,
      lookOf,
      widths: view.widths || {},
      fonts: view.fonts || {},
      align: view.align || {},
      baseFont: look.fontSize || null,
      totalsAt,
      pinned: view.pinned || [],
    })
  );
}
